import { Toaster } from "react-hot-toast"
import { parkingStickerRequestAttachmentKeys, ParkingStickerVehicle, USER_ROLES_IDS, userProfileAttachmentKeys } from "../../../constants/common"
import { useGetParkingStickerById } from "../../../hooks/permit/query"
import { useLocation } from "react-router-dom"
import Loader from "../../../components/loader"
import { createRef, MutableRefObject, useEffect, useMemo, useRef, useState } from "react"
import useAuthStore from "../../../store/Auth"
import { APP_ROUTES } from "../../../routes"
import useFormWithSchema from "../../../hooks/useFormWithSchema"
import { createIssuanceOfEntryStickerForParkingSchema, } from "../../../schemas/issuanceOfEntryStickerForParkingSchema"
import { useGetEmployeeDetails } from "../../../hooks/common/query"
import ActionButtonGroup from "./ActionButtonGroup"
import AccessDetails from "./AccessDetails"
import EmployeeInformation from "./EmployeeInformation"
import VehicleInformation from "./VehicleInformation"
import Acknowledgement from "./Acknowledgement"
import Attachments from "./Attachments"
import Header from "../Header"
import { useTranslation } from "react-i18next"
import { isCheckPermitApprovedByNextApprover, isPermitFieldsDisabled } from "../../../lib/functions"
import { useGetProfile } from "../../../hooks/user/query"

const ParkingStickerRequest = () => {
    const { t } = useTranslation()

    const location = useLocation()

    const { user } = useAuthStore()

    const searchParams = new URLSearchParams(location.search);

    const key = searchParams.get("ID");

    const { data: profileData } = useGetProfile();

    const { data: parkingSticker, isPending: isParkingStickerLoading } =
        useGetParkingStickerById({
            encryptedId: key || "",
        });

    const { data: employeeDetails } = useGetEmployeeDetails()

    const [addedVehicles, setAddedVehicles] = useState<ParkingStickerVehicle[]>([]);

    const [isAcknowledgeStickerReceipt, setIsAcknowledgeStickerReceipt] =
        useState(false);

    const [checkedBuildingIds, setCheckedBuildingIds] = useState<number[]>([]);
    const [vehicleAccessIds, setVehicleAccessIds] = useState<number[]>([]);

    const uploadRefs = useRef<
        Record<string, MutableRefObject<HTMLInputElement | null>>
    >(
        parkingStickerRequestAttachmentKeys.reduce((acc, key) => {
            acc[key] = createRef<HTMLInputElement>();
            return acc;
        }, {} as Record<string, MutableRefObject<HTMLInputElement | null>>)
    );

    const [fileNames, setFileNames] = useState<Record<string, string>>(
        parkingStickerRequestAttachmentKeys.reduce(
            (acc, key) => ({ ...acc, [key]: "" }),
            {}
        )
    );

    const [fileTypes, setFileTypes] = useState<Record<string, string>>(
        userProfileAttachmentKeys.reduce((acc, key) => ({ ...acc, [key]: "" }), {}),
    );

    const [filesBase64, setFilesBase64] = useState<Record<string, string>>(
        userProfileAttachmentKeys.reduce((acc, key) => ({ ...acc, [key]: "" }), {}),
    );

    const setFileName = (key: string, value: React.SetStateAction<string>) => {
        setFileNames((prev) => ({
            ...prev,
            [key]: typeof value === "function" ? value(prev[key]) : value,
        }));
    };

    const [isAttachments, setIsAttachments] = useState({
        haveEmployeeCard: 0,
        haveNationalId: 0,
    });

    const {
        control,
        formState: { errors },
        handleSubmit,
        setValue,
        clearErrors
    } = useFormWithSchema(createIssuanceOfEntryStickerForParkingSchema(isAttachments));

    useEffect(() => {
        if (profileData) {
            setIsAttachments({
                haveEmployeeCard: profileData.s_HostEmployeeCardFile,
                haveNationalId: profileData.s_HostNationalIDFile,
            });

            setFileTypes((prev) => ({
                ...prev,
                nationalId: profileData.s_HostNationalIDContentType || "",
                hostEmployeeCard: profileData.s_HostEmployeeCardContentType || "",
            }));

            setFilesBase64((prev) => ({
                ...prev,
                nationalId: profileData.s_HostNationalIDFile || "",
                hostEmployeeCard: profileData.s_HostEmployeeCardFile || "",
            }));
        }
    }, [profileData])

    useEffect(() => {
        if (employeeDetails) {
            setValue("name", employeeDetails.name || "");
            setValue("department", employeeDetails.department || "");
            setValue("jobTitle", employeeDetails.jobTitle || "");
            setValue("mobileNo", employeeDetails.mobileNo || "");
            setValue("employeeNumber", employeeDetails.employeeNumber || "");
        }
    }, [employeeDetails, setValue]);

    const [entryStickerDetails, setEntryStickerDetails] = useState<{
        id: number;
        name: string;
        department: string;
        jobTitle: string;
        mobileNo: string;
        officePhoneNo: string;
        employeeNumber: string;
    }>()

    useEffect(() => {
        if (!parkingSticker) {
            setEntryStickerDetails(undefined);

            setCheckedBuildingIds([]);
            setVehicleAccessIds([]);
            setAddedVehicles([]);

            setValue("name", employeeDetails?.name || "");
            setValue("department", employeeDetails?.department || "");
            setValue("jobTitle", employeeDetails?.jobTitle || "");
            setValue("mobileNo", employeeDetails?.mobileNo || "");
            setValue("officePhoneNo", "");
            setValue("employeeNumber", employeeDetails?.employeeNumber || "");

            setIsAcknowledgeStickerReceipt(false);

            if (!profileData) {
                setIsAttachments({
                    haveEmployeeCard: 0,
                    haveNationalId: 0,
                });
            }

            return;
        }

        const entrySticker = {
            id: parkingSticker.n_ParkingStickerRequestID,
            name: parkingSticker.s_Name,
            department: parkingSticker.s_Department,
            jobTitle: parkingSticker.s_JobTitle,
            mobileNo: parkingSticker.s_MobileNo,
            officePhoneNo: parkingSticker.s_OfficePhone,
            employeeNumber: parkingSticker.s_EmployeeNumber,
        }

        setEntryStickerDetails(entrySticker);

        Object.entries(entrySticker).forEach(([key, value]) => {
            setValue(key as any, value);
        });

        const buildings = parkingSticker.buildings?.map((building: { n_BuildingID: number }) => building.n_BuildingID) || [];
        setCheckedBuildingIds(buildings);

        const vehicleAccesses = parkingSticker.vehicleAccess?.map((access: { n_VehicleAccessID: number }) => access.n_VehicleAccessID) || [];
        setVehicleAccessIds(vehicleAccesses);

        const vehicles = parkingSticker.vehicles?.map((vehicle:
            { n_ParkingStickerVehicleID: number; n_PlateSource: number; n_PlateLetter1: number; n_PlateLetter2: number; n_PlateLetter3: number; s_PlateNumber: string; s_ModelNumber: string; s_Color: string; s_StickerNumber: string; s_CopyNumber: string; haveVehicleRegistrationFile: boolean }
        ) => ({
            id: vehicle.n_ParkingStickerVehicleID,
            plateSource: vehicle.n_PlateSource,
            plateLetter1: vehicle.n_PlateLetter1,
            plateLetter2: vehicle.n_PlateLetter2,
            plateLetter3: vehicle.n_PlateLetter3,
            plateNumber: vehicle.s_PlateNumber,
            model: vehicle.s_ModelNumber,
            color: vehicle.s_Color,
            stickerParkingNumber: vehicle.s_StickerNumber,
            copyNumber: vehicle.s_CopyNumber,
            uploadVehicleRegLicense: vehicle.haveVehicleRegistrationFile,
        })) || [];

        setAddedVehicles(vehicles);

        setIsAttachments({
            haveEmployeeCard: parkingSticker.haveHostEmployeeCardFile,
            haveNationalId: parkingSticker.haveNationalIDCardFile,
        });
    }, [parkingSticker, setValue, employeeDetails, profileData])

    const isPermitApprovedByNextApprover = useMemo(
        () => isCheckPermitApprovedByNextApprover(location.pathname, APP_ROUTES.UPDATE_PARKING_STICKER_REQUEST, parkingSticker?.n_CurrentApproverOrderNo),
        [location.pathname, parkingSticker?.n_CurrentApproverOrderNo]
    );

    const isFieldsDisabled = useMemo(
        () => isPermitFieldsDisabled(parkingSticker, isPermitApprovedByNextApprover, location.pathname, APP_ROUTES.APPROVE_PARKING_STICKER_REQUEST, user?.n_RoleID),
        [isPermitApprovedByNextApprover, location.pathname, user?.n_RoleID, parkingSticker]
    );

    return (
        <>
            <Toaster position="top-right" reverseOrder={false} />

            <div className="flex flex-col gap-5">
                <Header title={t("parkingStickerRequest")} data={parkingSticker} />

                {key && isParkingStickerLoading ? (
                    <div className="flex justify-center">
                        <Loader />
                    </div>
                ) : (
                    <>
                        <AccessDetails
                            isFieldsDisabled={isFieldsDisabled}
                            checkedBuildingIds={checkedBuildingIds}
                            setCheckedBuildingIds={setCheckedBuildingIds}
                            vehicleAccessIds={vehicleAccessIds}
                            setVehicleAccessIds={setVehicleAccessIds}
                        />

                        <EmployeeInformation
                            control={control}
                            errors={errors}
                            employeeDetails={employeeDetails}
                            isFieldsDisabled={isFieldsDisabled}
                        />

                        <VehicleInformation
                            addedVehicles={addedVehicles}
                            setAddedVehicles={setAddedVehicles}
                            isFieldsDisabled={isFieldsDisabled}
                        />

                        <Attachments
                            control={control}
                            errors={errors}
                            clearErrors={clearErrors}
                            isAttachments={isAttachments}
                            uploadRefs={uploadRefs.current}
                            fileNames={fileNames}
                            setFileName={setFileName}
                            isFieldsDisabled={isFieldsDisabled}
                            entryStickerDetails={entryStickerDetails}
                            fileTypes={fileTypes}
                            filesBase64={filesBase64}
                        />

                        {user?.n_RoleID === USER_ROLES_IDS.STAFF &&
                            location.pathname !== APP_ROUTES.APPROVE_PARKING_STICKER_REQUEST && <Acknowledgement
                                isAcknowledgeStickerReceipt={isAcknowledgeStickerReceipt}
                                setIsAcknowledgeStickerReceipt={setIsAcknowledgeStickerReceipt}
                                isFieldsDisabled={isFieldsDisabled}
                            />}

                        <ActionButtonGroup
                            parkingSticker={parkingSticker}
                            isPermitApprovedByNextApprover={isPermitApprovedByNextApprover}
                            checkedBuildingIds={checkedBuildingIds}
                            vehicleAccessIds={vehicleAccessIds}
                            addedVehicles={addedVehicles}
                            isAcknowledgeStickerReceipt={isAcknowledgeStickerReceipt}
                            isFieldsDisabled={isFieldsDisabled}
                            handleSubmit={handleSubmit}
                            filesBase64={filesBase64}
                            fileTypes={fileTypes}
                        />
                    </>
                )}
            </div>
        </>
    )
}

export default ParkingStickerRequest;
